/** @module Debug/RequestGenerator/Controller */
define([
	"dojo/_base/declare",
	"dojo/Deferred",
	"dojo/promise/all",
	"file-saver",
	"csi",
	"csi/_base/underscore",
	"csi/comm/PostRequest",
	"csi/def",
	"csi/form/Widget",
	"csi/html",
	"csi/prefs",
	"csi/reflection",
	"csi/sessionConstants",
	"csi/state",
	"csi/store/Classes",
	"csi/types",
	"csi/util",
	"csi/view",
	"csi/xml",
	"csi/action/Controller",
	"./ClassViewSelector",
	"./ViewContainer",
	"./TypeContainer",
	"./ColumnFilterValue",
	"./XMLGenerator"
], function(declare, Deferred, all, FileSaver, csi, _, PostRequest, def, FormWidget, html, csiPrefs, reflection, sessionConstants, state,
	StoreClasses, types, util, csiView, xml, Controller, ClassViewSelector, ViewContainer, TypeContainer, ColumnFilterValue, XMLGenerator) {

	// boolean switch to query for sample nodes
	var FETCH_SAMPLE_NODES = false;

	// Specify Party URLs to use custom views only shared with these parties. Set it to 'null' to use all custom views.
	var PARTIES_TO_CONSIDER = null; //[];

	// backup of sessionConstants while getting server requests for supplier views
	let _backupConstants;

	var _typeContainers;

	// filter function to filter out unnecessary custom views
	function needToProcessCustomView(pref) {
		if (!PARTIES_TO_CONSIDER)
			return true;

		var party = pref ? pref.getParty() : csi.EMPTY_URL;
		return _.indexOf(PARTIES_TO_CONSIDER, party) >= 0;
	}

	function needToProcessViewDefinition(defView) {
		return defView &&
			(defView.getType() != "Form") &&
			(defView.getType() != "Html") &&
			(defView.getType() != "Tabs") &&
			(defView.getType() != "Wizard");
	}

	function errorHandler() {
		var args = Array.prototype.slice.call(arguments);
		return function(error) {
			args.push(String(error));
			console.error.apply(console, args);
		};
	}

	function splitString(str, separator) {
		if (str) {
			return _.compact(_.map(str.split(separator || ","), function(s) {return s.trim();}));
		}

		return [];
	}

	/**
	 * Create the client-side node with the full set of attributes based on the reflection class
	 */
	function createClientNode(typeName) {
		var url = xml.getClientNodeUrl(typeName + "/");
		var node = xml.nodeCreateClientNode(url, typeName);

		var rc = reflection.getClass(typeName);
		if (rc) {
			_.each(rc.getAttributes(true, true), function(ra) {
				var attrId = ra.getId();

				// ignore existing attributes
				if (node[attrId] !== undefined)
					return;

				var type = ra.getType();

				var initialValue = ra.getValue();
				if (initialValue == null) {
					initialValue = xml.attributeTypeGetDefaultValue(type);
				}

				xml.nodeCreateClientAttr(node, attrId, type, initialValue);
			});
		}

		return node;
	}

	function _createViewContainer(view) {
		var viewId = view.defView.getId();
		var rootNode = view.getViewNode();
		var typeName = rootNode.$Type;

		_typeContainers = _typeContainers || {};

		var viewContainer = new ViewContainer(viewId, typeName, rootNode.$URL);
		var typeContainer = _typeContainers[typeName];
		if (!typeContainer) {
			typeContainer = _typeContainers[typeName] = new TypeContainer(typeName);
		}

		typeContainer.addViewContainer(viewContainer);
		return viewContainer;
	}

	function _getPreferences(view) {
		// get preferences for view and return pair of view and preferences
		var prefStore = view.get("customViewStore");
		if (prefStore) {
			return prefStore.query();
		}

		var dfd = new Deferred();
		dfd.resolve(null);
		return dfd;
	}

	function _loadClassesPerView(view, prefs) {
		// load all view classes for all preferences, and again return pair of view and preferences
		var loadDfds = [];

		if (prefs) {
			prefs = _.filter(prefs, needToProcessCustomView);
		}

		if (view) {
			loadDfds.push(view.loadViewClasses());

			_.each(prefs, function(pref) {
				view._applyCustomView(pref);
				loadDfds.push(view.loadViewClasses());
			});
		}

		return all(loadDfds).then(function() {
			return prefs;
		});
	}

	function _generateXMLDocumentForViewRequests(typeContainers, outputNode, fileName, typeName) {
		// summary: output XML script of requests of all views
		var OPEN_ROOT_TAG = "<ViewRequests>";
		var CLOSE_ROOT_TAG = "</ViewRequests>";

		// generate/serialize seperate XML documents for each type
		// to avoid potential memory allocation error during serialization of very large XML

		console.log("Starting generating XML");
		if (outputNode) {
			html.text(outputNode, OPEN_ROOT_TAG);

			_.each(typeContainers, function(typeContainer) {
				console.log("Generating XML for: " + typeContainer.typeName);
				var doc = XMLGenerator.generateXMLDocumentForTypeContainers([typeContainer]);
				var content = util.serializeXML(doc);
				html.text(outputNode, content.slice(OPEN_ROOT_TAG.length, -CLOSE_ROOT_TAG.length));
			});

			html.text(outputNode, CLOSE_ROOT_TAG);

			console.log("Finished generating XML.");
		} else {
			var docs = _.map(typeContainers, function(typeContainer) {
				return XMLGenerator.generateXMLDocumentForTypeContainers([typeContainer]);
			});

			console.log("Finished generating XML. Starting serializing XML");

			var contentParts = _.map(docs, function(doc) {
				var content = util.serializeXML(doc);
				content = content.slice(OPEN_ROOT_TAG.length, -CLOSE_ROOT_TAG.length);
				return content;
			});

			contentParts.unshift(OPEN_ROOT_TAG);
			contentParts.push(CLOSE_ROOT_TAG);

			console.log("Finished serializing XML. Starting creation of Blob");

			var blob = new Blob(contentParts, {type: "text/xml;charset=utf-8"});

			console.log("Finished creation of Blob. Starting saving file");

			if (typeName) {
				fileName += "_" + typeName;
			}

			FileSaver.saveAs(blob, fileName + ".xml", {autoBom: true});
			console.log("File saved.");
		}
	}

	function _setViewPreferences(view, pref) {
		// TODO: should probably move to function on view
		if (pref) {
			view._applyCustomView(pref);
		}

		view._resolveAttributeSpecs();

		view._resetStores();

		if (view._resetLevels) {
			view._resetLevels();
		}

		if (view._createQueryLevels) {
			view._createQueryLevels();

			// table view specific
			if (view._resolveColumns) {
				view.skipColumnMatrices = true;
				view._resolveColumns();
			}
		}
	}

	/**
	 * get Request objects from the widget
	 */
	function _getRequestsForWidgetAndCustomView(view, columnFilters) {
		var requests = [];
		var rootQuery = {};

		_.each(columnFilters, function(columnFilter) {
			if (columnFilter) {
				var value = columnFilter.value;
				if (!_.isArray(value))
					value = [value];
				rootQuery[columnFilter.queryKey] = value;
			}
		});

		// include all requests only when there is no filters
		view.collectRequestsForTesting(view.getViewNode(), rootQuery, requests, (columnFilters == null));
		return requests;
	}

	function getNodeOfType(/*ObjectHash*/sampleNodes, /*String*/typeName) {
		var node = sampleNodes[typeName];
		if (!node) {
			console.warn("Cannot find the node of type '" + typeName + "' on the server. Creating a fake client-side node.");
			node = createClientNode(typeName);
			sampleNodes[typeName] = node;
		}
		return node;
	}

	function getLongestColumnFilters(/*Object*/specMap, /*Number*/numberOfColumnFilters) {
		// sort by the path length in the descending order and get the first 'numberOfColumnFilters' longest ones
		var specs = _.toArray(specMap);

		specs.sort(function(s1, s2) {
			var steps1 = xml.pathGetSteps(s1.querySpec.path);
			var steps2 = xml.pathGetSteps(s2.querySpec.path);

			var len1 = steps1 ? steps1.length : 0;
			var len2 = steps2 ? steps2.length : 0;

			return len2 - len1;
		});

		specs = specs.slice(0, Math.max(numberOfColumnFilters, 0));

		return specs;
	}

	function adjustClassStoreItem(rc, item) {
		var className = rc.getType();
		if (item.name != className)
			item.name += " (" + className + ")";
	}

	/** Imitate a supplier for performance testing until resetSupplier is called */
	function imitateSupplier(userSupplierUrl, isAgent) {
		if (!_backupConstants) {
			_backupConstants = sessionConstants.getValues();
		}

		sessionConstants.initialize({
			..._backupConstants,
			isAgent,
			userSupplierUrl,
		});
	}

	/** Reset supplier to defaults for current user */
	function resetSupplier() {
		if (_backupConstants) {
			sessionConstants.initialize(_backupConstants);
		}
	}

	/**
	 * Controller
	 */
	return declare(Controller, {
		/** @property {string[]} classList - classes whose views should be analyzed; if empty, then all views will be analyzed */
		classList: null,

		/** @property {string} classViews - space-separated string of "class,view" pairs to analyze */
		classViews: null,

		/** @property {ClassViewSelection} classViewSelector - helper class for selecting class/views to process */
		classViewSelector: null,

		/** @property {string} fileName - name of file to output (default is "ViewRequests") */
		fileName: null,

		/** @property {number} numberOfColumnFilters - maximum number of filters to apply to view requests */
		numberOfColumnFilters: 0,

		/** @property {string} omitViews - comma-separated string of view Ids to omit from analysis */
		omitViews: null,

		/** @property {string} onlyViews - comma-separated string of view Ids to analyze */
		onlyViews: null,

		/** @property {DOMNode} outputNode - if specified, results are set as innerHTML to this node */
		outputNode: null,

		/** @property {boolean} saveByClass - save a file per class rather than all in one */
		saveByClass: false,

		/** @property {string} supplierUrl - URL of supplier to use to simulate logging in as portal user */
		supplierUrl: null,

		constructor: function() {
			this.classViewSelector = new ClassViewSelector();
		},

		onCreatePopup: function(/*csi.view.Widget*/view) {
			// summary: override getFieldStore method to return list of all classes
			view.getFieldStore = function(field, value) {
				var name = field.getControlName();
				if (name == "RequestGeneratorForm_SpecificClasses") {
					return StoreClasses(reflection.getClasses(), null, adjustClassStoreItem);
				}

				return FormWidget.prototype.getFieldStore.call(view, field, value);
			};

			this.inherited(arguments);
		},

		reportProgress: function(/*String*/s, /*boolean*/outputToConsole) {
			this.setProgress(s);
			if (outputToConsole)
				console.log(s);
		},

		process: function() {
			// fetch sample nodes for each node type and continue the processing after that
			var self = this;
			var cache = this.m_cache;
			var fd = this.m_formData;
			if (fd) {
				this.supplierUrl = fd.getFieldValue("RequestGeneratorForm_Supplier");
				this.numberOfColumnFilters = fd.getFieldValue("RequestGeneratorForm_NumberOfColumnFilters");
				this.classList = fd.getFieldValue("RequestGeneratorForm_SpecificClasses");
				this.onlyViews = fd.getFieldValue("RequestGeneratorForm_SpecificViewIds");
				this.omitViews = fd.getFieldValue("RequestGeneratorForm_IgnoringViewIds");
				this.classViews = fd.getFieldValue("RequestGeneratorForm_ClassViewIdPairs");
			}

			this.classViewSelector.setClassNames(this.classList);
			this.classViewSelector.setGlobalViewIdsToInclude(splitString(this.onlyViews));
			this.classViewSelector.setGlobalViewIdsToExclude(splitString(this.omitViews));

			_.each(splitString(this.classViews, /[\r\n]/g), function(strPair) {
				var pair = splitString(strPair);
				var className = pair[0];
				var viewId = pair[1];
				this.classViewSelector.addClassViewPair(className, viewId);
			}, this);

			this.when(cache.getNodeDeferred(this.supplierUrl)
				.then(function(supplier) {
					if (supplier) {
						imitateSupplier(supplier.$URL, supplier.IsAgent);
					}

					// close dialog to reveal the page and processing messages
					self.closeDialog();

					if (FETCH_SAMPLE_NODES) {
						self.reportProgress("Fetching sample nodes...", true);
						return new PostRequest("SiteAdmin", "GetSampleNodesOfAllTypes").post();
					}
				})
				.then(function(response) {
					var obj;

					if (response) {
						// create a map between the class name and the sample node
						cache.cacheResponse(response);
						obj = csi.hashArray(response.getNodes(), "$Type");
					} else {
						obj = {};
					}

					csiPrefs.resetStores();

					return self._generateViewRequestQueue(obj);
				})
				.then(function(queue) {
					return self._generateAllRequests(queue, self.numberOfColumnFilters);
				})
				.then(function() {
					return self._writeAllRequests();
				})
				.then(function() {
					resetSupplier();
					self.postProcess(true);
				})
			);
		},

		postProcess: function(/*Boolean*/bStatus, /*Boolean*/bRepeat) {
			// summary: reset supplier state and session context to original
			state.set("userSupplierUrl", "");
			state.set("isAgent", false);
			this.inherited(arguments);
		},

		_generateViewRequestQueue: function(/*Object?*/sampleNodes) {
			var dfd = new Deferred();
			var queue = [];

			// iterate over all classes
			var allClasses = this.classViewSelector.getReflectionClasses();

			var n = allClasses.length;
			var i = 0;

			csi.forEachWithTimeout(
				allClasses,
				5,
				null,
				function(rc) {
					this.reportProgress("Generating queue... " + Math.floor(100 * (i++ / n)) + "%");

					// does this class have any View XML?
					var typeName = rc.getType();
					var defType = def.typeDefinition(typeName);

					// no, it does not have any views - ignore it
					if (!defType)
						return;

					var concreteClasses = rc.abstract ? rc.getConcreteClasses() : [rc];
					if (!concreteClasses || !concreteClasses.length)
						return;

					// iterate over every view belonging to this class
					defType.traverseViewTags(function(viewTag) {
						var viewId = viewTag.getAttribute("Id");

						if (this.classViewSelector.shouldProcessView(viewId)) {
							// iterate over each concrete class of current class
							_.each(concreteClasses, function(cls) {
								if (!this.classViewSelector.shouldProcessViewForClass(cls, viewId))
									return;

								// get a sample node of this type or create a fake one
								var className = cls.getType();
								var rootNode = getNodeOfType(sampleNodes, className);

								// get XML view definition
								var defView = defType.getViewDef(viewId);

								// allow certain views to be omitted based on view definition
								if (!needToProcessViewDefinition(defView)) {
									return;
								}

								defView.m_rcRoot = cls;
								queue.push({type: className, id: viewId, node: rootNode, key: defView.getKey()});
							}, this);
						}
					}, this);
				},
				function() {
					dfd.resolve(queue);
				},
				this
			);

			return dfd;
		},

		_generateAllRequests: function(/*Object[]*/queue, /*Number*/numberOfColumnFilters) {
			// summary: load all views and generate requests of each view
			var self = this;
			var dfd = new Deferred();
			var viewParams = {cacheView: this.cacheView};
			var n = queue.length;
			var errors = [];
			var warnings = [];
			var i = 0;

			function processQueue() {
				var obj = queue[i++];
				if (!obj) {
					console.log("View Request Warnings: ", _.flatten(warnings));
					console.log("View Request Errors: ", errors);
					dfd.resolve();
					return;
				}

				var rootNode = obj.node;
				var viewId = obj.id;
				var defView = def.getNodeViewDef(rootNode, viewId);
				var currentView;

				// remove template from Html views so it will not try to load it (and complain when we delete the view before it is loaded)
				if (defView) {
					defView.templatePath = null;
				}

				// load the widget corresponding to the view, and then process all its custom views
				console.log(`Processing view ${viewId} for type ${rootNode.$Type}`);

				try {
					csiView.newViewDeferred(defView, rootNode, viewParams, true)
					.then(function(view) {
						currentView = view;
						return _getPreferences(view);
					})
					.then(function(prefs) {
						return _loadClassesPerView(currentView, prefs);
					})
					.then(function(prefs) {
						return self._getRequestsPerView(currentView, prefs, numberOfColumnFilters, "Generating requests... " + Math.floor(100 * (i / n)) + "%");
					})
					.then(
						function(viewWarnings) {
							warnings.push(viewWarnings);
						},
						function(err) {
							obj.err = err;
							errors.push(obj);
							return true;
						}
					)
					.then(
						function() {
							// cleanup
							if (currentView) {
								currentView.destroyView();
								currentView = null;
							}

							// continue with next view
							_.defer(processQueue);
						}
					);
				} catch (error) {
					console.error(`Processing view ${viewId} for type ${rootNode.$Type} failed with error:`, error);

					// continue with next view
					_.defer(processQueue);
				}
			}

			console.log("View Request Queue: ", _.pluck(queue, "key"));
			_typeContainers = null;
			processQueue();

			return dfd;
		},

		_getRequestsPerView: function(/*csi.view.Widget*/view, /*PreferenceView[]*/prefs, /*Number*/numberOfColumnFilters, /*String*/progress) {
			var dfd = new Deferred();
			var warnings = [];
			var i = 0;
			var n = prefs ? prefs.length : 0;

			if (!prefs) {
				prefs = [null];
			}

			csi.forEachWithTimeout(
				prefs,
				5,
				null,
				function(pref) {
					try {
						var progressPrefix = view.defView.getKey();

						if (n > 1) {
							progressPrefix += " - " + Math.floor(100 * (i++ / n)) + "%";
						}

						this.reportProgress("(" + progressPrefix + ") " + progress);

						// apply custom view preferences
						_setViewPreferences(view, pref);

						// collect requests for each custom view (including default view)
						var viewContainer = _createViewContainer(view);
						viewContainer.pref = pref;
						viewContainer.requests = _getRequestsForWidgetAndCustomView(view);

						// do not add column filters if the view is slim
						if (view.defView.isSlim()) {
							return;
						}

						// do not add column filters if the view doesn't contain 'filter' plugin
						if (!view.hasPlugin("filter")) {
							return;
						}

						// add column specs for view
						var querySpecStore = view.get("querySpecStore");
						if (!querySpecStore) {
							return;
						}

						var columnFiltersMap = {};

						querySpecStore.query().forEach(function(item) {
							if (item.spec.canFilter(true)) {
								columnFiltersMap[item.id] = item.spec;
							}
						});

						// generate requests for longest column filters
						var columnFilters = getLongestColumnFilters(columnFiltersMap, numberOfColumnFilters);

						var columnFilterValues = _.map(columnFilters, function(spec) {
							// generate the request for the drop-down
							var store = view.getSpecValues(spec.queryKey, {});
							if (store) {
								viewContainer = _createViewContainer(view);
								viewContainer.pref = pref;
								viewContainer.spec = spec;
								viewContainer.requests = [store.getRequest()];

								// put the arbitrary value as the column value depending on the column type
								var attrType = spec.querySpec.type;
								if (attrType) {
									var arbitraryValue = xml.attributeTypeGetDefaultValue(attrType);
									if (_.isString(arbitraryValue)) {
										arbitraryValue = "someString";
									} else if (spec.isDateAttribute()) {
										arbitraryValue = types.getDateRange(arbitraryValue);
									}

									return new ColumnFilterValue(arbitraryValue, attrType, spec.queryKey);
								}
							}

							warnings.push("_getRequestsPerView: No filter store for spec '" + spec.queryKey + "' in view " + view.defView.getKey());
							return null;
						});

						// filter out null values
						columnFilterValues = _.compact(columnFilterValues);

						if (columnFilterValues.length) {
							// apply column filters progressively one by one
							_.each(columnFilterValues, function(columnFilterValue, index) {
								var filterValues = columnFilterValues.slice(0, index + 1);
								viewContainer = _createViewContainer(view);
								viewContainer.pref = pref;
								viewContainer.columnFilterValues = filterValues;
								viewContainer.requests = _getRequestsForWidgetAndCustomView(view, filterValues);
							});

							// apply all blank filters
							var allBlankFilterValues = _.map(columnFilterValues, function(columnFilterValue) {
								var value = types.supportsDateRange(columnFilterValue.type) ? types.getDateRange(null, null, types.DateRangeSelection.BLANKS) : xml.VALUE_BLANK;
								return new ColumnFilterValue(value, null, columnFilterValue.queryKey);
							});

							viewContainer = _createViewContainer(view);
							viewContainer.pref = pref;
							viewContainer.columnFilterValues = allBlankFilterValues;
							viewContainer.requests = _getRequestsForWidgetAndCustomView(view, allBlankFilterValues);
						}
					} catch (error) {
						warnings.push("_getRequestsPerView: error occurred in view " + view.defView.getKey() + ". Error=" + error);
					}
				},
				function() {
					dfd.resolve(warnings);
				},
				this
			);

			return dfd;
		},

		_writeAllRequests: function() {
			var fileName = this.fileName || "ViewRequests";
			var outputNode = this.outputNode;

			if (this.saveByClass) {
				_.each(_typeContainers, function(typeContainer, typeName) {
					this.reportProgress("Saving requests for class " + typeName + "...", true);
					_generateXMLDocumentForViewRequests([typeContainer], outputNode, fileName, typeName);
				}, this);
			} else {
				this.reportProgress("Saving requests...", true);
				_generateXMLDocumentForViewRequests(_.values(_typeContainers), outputNode, fileName);
			}

			_typeContainers = null;
		}
	});
});
